import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MobileLogo from '../Layout/MobileLogo';
import '../../styles/ReportDoc.css';

/* Итоги недели листом A4 — в том же стиле, что бланк варианта.
   Содержимое приходит снимком из уведомления, поэтому файл, скачанный через
   месяц, покажет ровно те цифры, что были в воскресенье. Страницы режем сами:
   блоки меряем за экраном и набиваем листы доверху. */
const PAGE_H = 1123;
const PAD_TOP = 64, PAD_BOTTOM = 54;
const LOGO = `${process.env.PUBLIC_URL || ''}/landing/logo-trim.png`;

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const daysAgo = (iso) => {
  if (!iso) return 'активности не было';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'сегодня';
  if (days === 1) return 'вчера';
  return `${days} ${plural(days, ['день', 'дня', 'дней'])} назад`;
};

const Tile = ({ value, sub, caption, delta }) => (
  <div className="rep-tile">
    <div className="rep-tile-v">
      {value}{sub && <small> {sub}</small>}
      {delta !== undefined && delta !== null && (
        <span className={`rep-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'same'}`}>
          {delta > 0 ? `+${delta}` : delta < 0 ? delta : '='}
        </span>
      )}
    </div>
    <div className="rep-tile-c">{caption}</div>
  </div>
);

const Block = ({ cap, children }) => (
  <>
    {cap && <div className="rep-cap">{cap}</div>}
    {children}
  </>
);

// Колонки таблицы учеников в сводке админа
const SUM_COLS = '1.6fr 0.62fr 0.5fr 0.5fr 0.62fr 0.8fr';

function buildBlocks(r) {
  const blocks = [];
  const add = (key, node) => blocks.push({ key, node });

  if (r.kind === 'digest') {
    const d = r;
    add('tiles', (
      <Block cap="Главное за неделю">
        <div className="rep-tiles">
          <Tile value={`${d.webinars.attended}`} sub={`из ${d.webinars.held}`} caption="вебинаров посмотрел" />
          <Tile value={`${d.homework.done}`} caption={`ДЗ сдано · ${d.homework.correctPct}% верных`} />
          <Tile value={`${d.variants.done}`} caption="вариантов решено" />
          <Tile value={`${d.activeDays}`} sub="из 7" caption="дней с занятиями" />
        </div>
      </Block>
    ));
    add('progress', (
      <Block cap="Прогресс по курсу">
        <div className="rep-bar"><i style={{ width: `${Math.min(100, d.progress.now)}%` }} /></div>
        <div className="rep-bar-l">
          <span>было {d.progress.was}%</span>
          <span>
            стало {d.progress.now}%
            <span className={`rep-delta ${d.progress.now - d.progress.was > 0 ? 'up' : 'same'}`}>
              {d.progress.now - d.progress.was > 0 ? `+${d.progress.now - d.progress.was}` : '='}
            </span>
          </span>
        </div>
      </Block>
    ));
    add('weak', (
      <Block cap="Над чем поработать">
        {d.weakSpots?.length ? (
          <div className="rep-list">
            {d.weakSpots.map(w => (
              <div className="rep-li" key={w.num}>
                <span className="rep-dot" />
                <b>Номер {w.num}</b>
                <span className="dim">{w.count} {plural(w.count, ['ошибка', 'ошибки', 'ошибок'])} за неделю — стоит повторить</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rep-note">
            {d.homework.answers > 0
              ? 'Ошибок за неделю не было — отличная работа.'
              : 'На этой неделе заданий не решал, поэтому и разбирать пока нечего.'}
          </div>
        )}
      </Block>
    ));
    add('next', (
      <Block cap="На следующей неделе">
        <div className="rep-list">
          {d.next.webinars.length
            ? d.next.webinars.map((w, i) => (
              <div className="rep-li" key={i}>
                <span className="rep-dot" />
                <b>{w.title}</b>
                <span className="dim">{fmtWhen(w.at)}</span>
              </div>
            ))
            : <div className="rep-li"><span className="rep-dot" /><span className="dim">Вебинаров в расписании пока нет</span></div>}
          <div className="rep-li">
            <span className="rep-dot" />
            <b>{d.next.homeworkLeft} {plural(d.next.homeworkLeft, ['ДЗ', 'ДЗ', 'ДЗ'])}</b>
            <span className="dim">и {d.next.variantsLeft} {plural(d.next.variantsLeft, ['вариант', 'варианта', 'вариантов'])} ждут решения</span>
          </div>
        </div>
      </Block>
    ));
    return blocks;
  }

  // Сводка админа
  const s = r;
  add('totals', (
    <Block cap="Общее">
      <div className="rep-tiles">
        <Tile value={`${s.totals.active}`} sub={`из ${s.totals.students}`} caption="учеников занимались" />
        <Tile value={`${s.totals.newSelf + s.totals.newAdded}`} caption={`новых · сами ${s.totals.newSelf}, добавлены ${s.totals.newAdded}`} />
        <Tile value={`${s.totals.webinarsHeld}`} caption="вебинаров прошло" />
        <Tile value={`${s.totals.silent}`} caption="без активности" />
      </div>
    </Block>
  ));
  add('table-head', (
    <Block cap="Кто чем занимался">
      <div className="rep-row head" style={{ gridTemplateColumns: SUM_COLS }}>
        <span>Ученик</span><span>Вебинары</span><span>ДЗ</span><span>Вар.</span><span>Верных</span><span>Прогресс</span>
      </div>
    </Block>
  ));
  (s.rows || []).forEach((row, i) => add(`row-${row.id}`, (
    <div className={`rep-row${i % 2 ? ' zebra' : ''}`} style={{ gridTemplateColumns: SUM_COLS }}>
      <span style={{ minWidth: 0 }}>
        <span className="name">{row.name || '—'}</span>
        <span className="mail" style={{ display: 'block' }}>{row.course}</span>
      </span>
      <span className="rep-num">{row.webinars}</span>
      <span className="rep-num">{row.hwDone}</span>
      <span className="rep-num">{row.variants}</span>
      <span className="rep-num">{row.answers ? `${row.correctPct}%` : '—'}</span>
      <span className="rep-num">
        {row.progressNow}%
        {row.progressNow - row.progressWas > 0 && <span className="rep-delta up">+{row.progressNow - row.progressWas}</span>}
      </span>
    </div>
  )));
  if (s.attention?.silent?.length) {
    add('silent', (
      <Block cap="Пропали из виду">
        <div className="rep-list">
          {s.attention.silent.map(x => (
            <div className="rep-li" key={x.email}>
              <span className="rep-dot" /><b>{x.name || x.email}</b>
              <span className="dim">{daysAgo(x.lastActive)}</span>
            </div>
          ))}
        </div>
      </Block>
    ));
  }
  if (s.attention?.expiring?.length) {
    add('expiring', (
      <Block cap="Доступ кончается на этой неделе">
        <div className="rep-list">
          {s.attention.expiring.map(x => (
            <div className="rep-li" key={x.email}>
              <span className="rep-dot" /><b>{x.name || x.email}</b>
              <span className="dim">до {fmtDate(x.accessUntil)}</span>
            </div>
          ))}
        </div>
      </Block>
    ));
  }
  if (s.webinars?.length) {
    add('webinars', (
      <Block cap="Вебинары недели">
        <div className="rep-list">
          {s.webinars.map((w, i) => (
            <div className="rep-li" key={i}>
              <span className="rep-dot" /><b>{w.title}</b>
              <span className="dim">{fmtWhen(w.at)} · пришли {w.attended} из {w.of}</span>
            </div>
          ))}
        </div>
      </Block>
    ));
  }
  return blocks;
}

export default function ReportDoc({ report, onReady, pagesRef }) {
  const [pages, setPages] = useState(null);
  const rulerRef = useRef(null);
  const headRef = useRef(null);
  const slimRef = useRef(null);
  const footRef = useRef(null);
  const ownRef = useRef(null);
  const boxRef = pagesRef || ownRef;

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Oswald:wght@500;600&display=block';
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  const blocks = useMemo(() => (report ? buildBlocks(report) : []), [report]);

  const layout = useCallback(() => {
    const ruler = rulerRef.current;
    if (!ruler || !blocks.length) return;
    const nodes = [...ruler.querySelectorAll('[data-block]')];
    if (nodes.length !== blocks.length) return;
    const H = nodes.map(n => n.getBoundingClientRect().height);
    const headH = headRef.current?.offsetHeight || 0;
    const slimH = slimRef.current?.offsetHeight || 0;
    const footH = footRef.current?.offsetHeight || 0;
    const usable = (first) => PAGE_H - PAD_TOP - PAD_BOTTOM - (first ? headH : slimH) - footH;

    const out = [];
    let page = [], left = usable(true);
    blocks.forEach((b, i) => {
      const margin = page.length ? 20 : 0;
      if (page.length && H[i] + margin > left) {
        out.push(page);
        page = [i];
        left = usable(false) - H[i];
      } else {
        page.push(i);
        left -= H[i] + margin;
      }
    });
    if (page.length) out.push(page);
    setPages(out);
  }, [blocks]);

  useEffect(() => {
    if (!blocks.length) return undefined;
    let cancelled = false;
    (async () => {
      try { await document.fonts.ready; } catch { /* нет поддержки — не беда */ }
      if (!cancelled) layout();
    })();
    return () => { cancelled = true; };
  }, [blocks.length, layout]);

  useEffect(() => {
    if (!pages || !onReady) return undefined;
    const t = setTimeout(() => onReady([...(boxRef.current?.querySelectorAll('.rep-page') || [])]), 60);
    return () => clearTimeout(t);
  }, [pages, onReady, boxRef]);

  if (!report) return null;

  const isDigest = report.kind === 'digest';
  const title = isDigest ? 'Итоги недели' : 'Сводка за неделю';
  const meta = isDigest
    ? [report.student?.name, (report.courses || []).join(', ')].filter(Boolean)
    : [`${report.totals.students} ${plural(report.totals.students, ['ученик', 'ученика', 'учеников'])}`,
       `${report.totals.active} занимались`];

  const Head = ({ slim }) => (
    <>
      <div className={`rep-head${slim ? ' slim' : ''}`}>
        <div className="rep-head-l">
          {!slim && <img className="rep-logo" src={LOGO} alt="ИНФА СОТКА" />}
          <div className="rep-title">{title}</div>
        </div>
        <div className="rep-when">{report.week?.label}</div>
      </div>
      {slim && <div className="rep-head-gap" />}
      {!slim && <div className="rep-meta">{meta.map((m, i) => <span key={i}>{m}</span>)}</div>}
    </>
  );

  const list = pages || [blocks.map((_, i) => i)];
  return (
    <>
      <div className="rep-pages" ref={boxRef}>
        {list.map((idxs, pi) => (
          <div className="rep-page" key={pi}>
            <Head slim={pi > 0} />
            <div className="rep-blocks">
              {idxs.map((i, k) => (
                <div className={`rep-block${k === 0 ? ' top' : ''}`} key={blocks[i].key}>{blocks[i].node}</div>
              ))}
            </div>
            <div className="rep-foot">
              <MobileLogo />
              <span className="rep-pnum">{pi + 1} / {list.length}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="rep-ruler" ref={rulerRef} aria-hidden="true">
        <div className="rep-ruler-page">
          <div ref={headRef}><Head /></div>
          <div ref={slimRef}><Head slim /></div>
          <div ref={footRef}><div className="rep-foot"><MobileLogo /><span className="rep-pnum">1 / 1</span></div></div>
          {blocks.map(b => <div data-block key={b.key}>{b.node}</div>)}
        </div>
      </div>
    </>
  );
}
